import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Layout from '../components/Layout';
import { Building, Plus, Edit2, Trash2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function DepartmentMaster({ user, onLogout }) {
  const [departments, setDepartments] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingDept, setEditingDept] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    head_id: '',
    budget: ''
  });

  useEffect(() => {
    fetchDepartments();
  }, []);

  const fetchDepartments = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/departments`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDepartments(response.data || []);
    } catch (error) {
      console.error('Failed to fetch departments:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      
      if (editingDept) {
        await axios.put(`${API}/departments/${editingDept.id}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        alert('Department updated successfully!');
      } else {
        await axios.post(`${API}/departments`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        alert('Department created successfully!');
      }

      resetForm();
      fetchDepartments();
    } catch (error) {
      alert('Failed to save department: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleEdit = (dept) => {
    setEditingDept(dept);
    setFormData({
      name: dept.name,
      description: dept.description || '',
      head_id: dept.head_id || '',
      budget: dept.budget || ''
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this department?')) return;

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/departments/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      alert('Department deleted successfully!');
      fetchDepartments();
    } catch (error) {
      alert('Failed to delete department: ' + (error.response?.data?.detail || error.message));
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      head_id: '',
      budget: ''
    });
    setEditingDept(null);
    setShowForm(false);
  };

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="p-6">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-3xl font-bold" style={{color: '#2b3380'}}>
              Department Master
            </h1>
            <p className="text-gray-600 mt-1">Manage organizational departments</p>
          </div>
          <button
            onClick={() => {
              setShowForm(!showForm);
              if (showForm) resetForm();
            }}
            className="px-6 py-3 rounded-lg text-white font-medium flex items-center gap-2"
            style={{backgroundColor: '#2b3380'}}
          >
            <Plus className="w-5 h-5" />
            {showForm ? 'Cancel' : 'Add Department'}
          </button>
        </div>

        {showForm && (
          <div className="bg-white rounded-lg shadow-md p-6 mb-6">
            <h2 className="text-xl font-bold mb-4" style={{color: '#2b3380'}}>
              {editingDept ? 'Edit Department' : 'Add New Department'}
            </h2>
            <form onSubmit={handleSubmit}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Department Name *</label>
                  <input
                    type="text"
                    placeholder="e.g., Sales, Engineering"
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    className="w-full px-3 py-2 border rounded-lg"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Annual Budget</label>
                  <input
                    type="number"
                    placeholder="e.g., 5000000"
                    value={formData.budget}
                    onChange={(e) => setFormData({...formData, budget: e.target.value})}
                    className="w-full px-3 py-2 border rounded-lg"
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium mb-2">Description</label>
                  <textarea
                    placeholder="Brief description of this department"
                    value={formData.description}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                    className="w-full px-3 py-2 border rounded-lg"
                    rows="2"
                  />
                </div>
              </div>

              <div className="flex gap-2">
                <button
                  type="submit"
                  className="px-6 py-2 rounded-lg text-white"
                  style={{backgroundColor: '#2b3380'}}
                >
                  {editingDept ? 'Update Department' : 'Create Department'}
                </button>
                <button
                  type="button"
                  onClick={resetForm}
                  className="px-6 py-2 rounded-lg border"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {departments.length === 0 ? (
            <div className="col-span-full bg-white rounded-lg shadow-md p-8 text-center">
              <Building className="w-12 h-12 mx-auto mb-2 text-gray-300" />
              <p className="text-gray-500">No departments configured yet</p>
            </div>
          ) : (
            departments.map((dept) => (
              <div key={dept.id} className="bg-white rounded-lg shadow-md p-5 hover:shadow-lg transition-shadow">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <Building className="w-6 h-6" style={{color: '#2b3380'}} />
                    <h3 className="text-lg font-bold" style={{color: '#2b3380'}}>
                      {dept.name}
                    </h3>
                  </div>
                  <span className="px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                    Active
                  </span>
                </div>

                {dept.description && (
                  <p className="text-sm text-gray-600 mb-4">{dept.description}</p>
                )}

                {dept.budget && (
                  <p className="text-sm text-gray-500 mb-4">
                    Budget: ₹{parseInt(dept.budget).toLocaleString('en-IN')}
                  </p>
                )}

                <div className="flex gap-2 mt-4">
                  <button
                    onClick={() => handleEdit(dept)}
                    className="flex-1 px-3 py-2 text-sm text-blue-600 hover:bg-blue-50 rounded-lg transition-colors flex items-center justify-center gap-1"
                  >
                    <Edit2 className="w-3 h-3" />
                    Edit
                  </button>
                  <button
                    onClick={() => handleDelete(dept.id)}
                    className="flex-1 px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded-lg transition-colors flex items-center justify-center gap-1"
                  >
                    <Trash2 className="w-3 h-3" />
                    Delete
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </Layout>
  );
}

export default DepartmentMaster;